import { Column, EmptyDiv, FormContext, InfoDiv } from '@/components/SettingsLayout';
import SkeletonList from '@/components/SkeletonLoader/SkeletonList';
import {
  useGetDefaultPricingConfigurationQuery,
  useGetPricingConfigurationQuery,
} from '@/store/slices/pricingConfigurationSlice';
import { useCreateOrUpdateCustomPlanMutation } from '@/store';
import { Text, Button, Icon, IconType } from '@/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Company } from '@/context/types';
import { Plan } from '@/services/Plans/plans.service';
import PricingConfigurationForm from '@/pages/pricing-forms/PricingConfigurationForm';
import PricingDiffFooter from '@/pages/pricing-forms/PricingDiffFooter';
import { usePricingForm } from '@/pages/pricing-forms/usePricingForm';
import { PricingConfigurationValues } from '@/services/Pricing/pricing-configuration.service';

type Props = {
  company: Company;
  plan: Plan;
};

function CompanyPricing({ company, plan }: Props) {
  const { t } = useTranslation();
  const [isReadOnly, setIsReadOnly] = useState(true);

  const orgContext = { organizationId: company.organizationId };
  const planContext = { organizationId: company.organizationId, planId: plan._id };

  const { data: orgPricing, isLoading: isOrgPricingLoading } =
    useGetPricingConfigurationQuery(orgContext);
  const { data: systemDefaults, isLoading: isDefaultsLoading } =
    useGetDefaultPricingConfigurationQuery(orgContext);
  const { data: existing, isLoading: isExistingLoading } =
    useGetPricingConfigurationQuery(planContext);

  const [createOrUpdateCustomPlan, { isLoading: isSaving }] = useCreateOrUpdateCustomPlanMutation();

  const baseline: PricingConfigurationValues = useMemo(
    () => ({
      ...(systemDefaults?.values ?? {}),
      ...(orgPricing?.values ?? {}),
    }),
    [systemDefaults, orgPricing],
  );

  const { activeCountry, setActiveCountry, submitHandler } = usePricingForm({
    baseline,
    existing: existing?.values,
    submit: (nextValues) =>
      createOrUpdateCustomPlan({
        organizationId: company.organizationId,
        companyId: company._id,
        pricingData: nextValues,
      }),
    messages: {
      success: t('Messages.plan_update_success'),
      error: t('Messages.plan_update_error'),
    },
  });

  if (isOrgPricingLoading || isDefaultsLoading || isExistingLoading) {
    return <SkeletonList />;
  }

  if (!systemDefaults) {
    return (
      <EmptyDiv>
        <Text size='s' weight='medium'>
          {t('Errors.error_loading_pricing')}
        </Text>
      </EmptyDiv>
    );
  }

  return (
    <FormContext submitHandler={submitHandler} mode='onChange' defaultValues={{ rows: [] }}>
      <Column gapSize='24'>
        {isReadOnly && (
          <InfoDiv>
            <Text size='s' weight='light' color='base-grey'>
              {t('Pricing.organization_pricing_comment')}
            </Text>
            <Button type='button' variant='outline' onClick={() => setIsReadOnly(false)}>
              <Icon type={IconType.Pencil_Edit} width={24} height={24} />
              <Text size='s' weight='medium' color='base-grey'>
                {t('Pricing.customize')}
              </Text>
            </Button>
          </InfoDiv>
        )}
        <Column>
          <Text size='m' weight='semi-bold'>
            {t('Accounts.processing_fees')}
          </Text>
          <Text size='s' weight='light' color='base-grey'>
            {t('Accounts.processing_fees_comment')}
          </Text>
          <PricingConfigurationForm
            mode='company'
            defaults={baseline}
            orgPricing={baseline}
            existingValues={existing?.values}
            isReadOnly={isReadOnly}
            activeCountry={activeCountry}
            onActiveCountryChange={setActiveCountry}
          />
        </Column>
        <PricingDiffFooter
          baseline={baseline}
          existing={existing?.values}
          isSaving={isSaving}
          isReadOnly={isReadOnly}
          activeCountry={activeCountry}
        />
      </Column>
    </FormContext>
  );
}

export default CompanyPricing;
